/**
 * GovernancePage — Proxy CAA + Règles d'accès par clearance (S5.5-02)
 *
 * Données : statiques (règles de routage issues de policy.yaml côté backend).
 * Bouton "Recharger la politique" : POST /api/v1/admin/settings/reload-policy
 *
 * Contrainte : zéro boucle for/while — style fonctionnel strict.
 */
import React, { useCallback, useState } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Globe,
  Server,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { useSDK } from '../context/SDKContext.tsx';

// ── Types ─────────────────────────────────────────────────────────────────────

type Clearance = 'C1' | 'C2' | 'C3' | 'C4';
type Zone = 'cloud' | 'on_prem';
type Provider = 'Mistral Cloud' | 'Google AI' | 'Anthropic' | 'Mistral On-Prem';

interface ModelAccessRule {
  clearance: Clearance;
  label: string;
  description: string;
  zone: Zone;
  providers: Provider[];
  model: string;
  regulatory: boolean;
}

interface ProxyStatus {
  enabled: boolean;
  url: string;
  ntlm: boolean;
  noProxy: string[];
}

// ── Données statiques (issues de policy.yaml + models.yaml) ──────────────────

const MODEL_ACCESS_RULES: ModelAccessRule[] = [
  {
    clearance: 'C1',
    label: 'Public',
    description: 'Données publiques, aucune restriction',
    zone: 'cloud',
    providers: ['Mistral Cloud', 'Google AI', 'Anthropic'],
    model: 'mistral-small-latest',
    regulatory: false,
  },
  {
    clearance: 'C2',
    label: 'Interne',
    description: 'Documents internes sans données sensibles',
    zone: 'cloud',
    providers: ['Mistral Cloud', 'Google AI', 'Anthropic'],
    model: 'mistral-small-latest',
    regulatory: false,
  },
  {
    clearance: 'C3',
    label: 'Sensible',
    description: 'Données patrimoniales, successorales ou financières',
    zone: 'cloud',
    providers: ['Mistral Cloud'],
    model: 'mistral-small-latest',
    regulatory: false,
  },
  {
    clearance: 'C4',
    label: 'Très sensible',
    description: 'Données médicales — contrainte réglementaire ANSSI',
    zone: 'on_prem',
    providers: ['Mistral On-Prem'],
    model: 'mistral-large-local',
    regulatory: true,
  },
];

const PROXY_CONFIG: ProxyStatus = {
  enabled: true,
  url: 'http://127.0.0.1:3128',
  ntlm: false,
  noProxy: ['mistral-onprem.caa01.local', '*.caa01.local', '127.*', 'localhost'],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const CLEARANCE_STYLES: Record<Clearance, { bg: string; text: string; border: string }> = {
  C1: { bg: 'bg-green-500/10',  text: 'text-green-700',  border: 'border-green-500/30' },
  C2: { bg: 'bg-blue-500/10',   text: 'text-blue-700',   border: 'border-blue-500/30' },
  C3: { bg: 'bg-yellow-500/10', text: 'text-yellow-700', border: 'border-yellow-500/30' },
  C4: { bg: 'bg-red-500/10',    text: 'text-red-700',    border: 'border-red-500/30' },
};

const CLEARANCE_ICONS: Record<Clearance, React.ComponentType<{ size?: number; className?: string }>> = {
  C1: ShieldCheck,
  C2: Shield,
  C3: ShieldAlert,
  C4: ShieldOff,
};

// ── Sous-composants ───────────────────────────────────────────────────────────

function ClearanceBadge({ clearance }: { clearance: Clearance }): React.ReactElement {
  const s = CLEARANCE_STYLES[clearance];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${s.bg} ${s.text} ${s.border}`}>
      {clearance}
    </span>
  );
}

function ZonePill({ zone }: { zone: Zone }): React.ReactElement {
  return zone === 'on_prem' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-700 border border-red-500/20">
      <Server size={10} />
      On-Prem uniquement
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-700 border border-blue-500/20">
      <Globe size={10} />
      Cloud autorisé
    </span>
  );
}

interface ModelAccessCardProps {
  rule: ModelAccessRule;
}

function ModelAccessCard({ rule }: ModelAccessCardProps): React.ReactElement {
  const Icon = CLEARANCE_ICONS[rule.clearance];
  const s = CLEARANCE_STYLES[rule.clearance];

  return (
    <div className={`rounded-lg border p-5 shadow-sm ${s.border} ${s.bg}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={20} className={s.text} />
          <div>
            <div className="flex items-center gap-2">
              <ClearanceBadge clearance={rule.clearance} />
              <span className="text-sm font-semibold text-foreground">{rule.label}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
          </div>
        </div>
        <ZonePill zone={rule.zone} />
      </div>

      {rule.regulatory && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20">
          <ShieldOff size={14} className="text-red-600 shrink-0" />
          <p className="text-xs text-red-700 font-medium">
            Contrainte réglementaire ANSSI — aucun provider cloud autorisé
          </p>
        </div>
      )}

      <div className="space-y-2">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Providers autorisés</p>
          <div className="flex flex-wrap gap-1">
            {rule.providers.map((p) => (
              <span key={p} className="px-2 py-0.5 rounded text-xs bg-card border border-border text-foreground">
                {p}
              </span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-0.5">Modèle par défaut</p>
          <code className="text-xs bg-muted px-1.5 py-0.5 rounded text-foreground font-mono">
            {rule.model}
          </code>
        </div>
      </div>
    </div>
  );
}

interface ProxyWidgetProps {
  config: ProxyStatus;
}

function ProxyGouvernanceWidget({ config }: ProxyWidgetProps): React.ReactElement {
  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Globe size={16} className="text-primary" />
          Proxy réseau CAA
        </h3>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
          config.enabled
            ? 'bg-green-500/10 text-green-700 border border-green-500/20'
            : 'bg-muted text-muted-foreground border border-border'
        }`}>
          {config.enabled ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
          {config.enabled ? 'Actif' : 'Inactif'}
        </span>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">URL proxy</span>
          <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono text-foreground">
            {config.url}
          </code>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Authentification NTLM</span>
          <span className={`text-xs font-medium ${config.ntlm ? 'text-yellow-600' : 'text-green-600'}`}>
            {config.ntlm ? 'Activée' : 'Désactivée (Sprint 5.2)'}
          </span>
        </div>
        <div className="py-2">
          <p className="text-muted-foreground mb-2">Hôtes exclus (NO_PROXY)</p>
          <div className="flex flex-wrap gap-1">
            {config.noProxy.map((h) => (
              <code key={h} className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono text-foreground">
                {h}
              </code>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export default function GovernancePage(): React.ReactElement {
  const { sdk } = useSDK();
  const [reloading, setReloading] = useState(false);
  const [reloadMsg, setReloadMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const handleReloadPolicy = useCallback(async () => {
    setReloading(true);
    setReloadMsg(null);
    try {
      // POST /api/v1/admin/settings/reload-policy via RestClient post helper
      const { data } = await sdk.http.post<{ status: string; message: string }>(
        '/api/v1/admin/settings/reload-policy',
        {},
        async () => ({ status: 'ok', message: 'Politique rechargée (simulation)' })
      );
      setReloadMsg({ ok: data.status === 'ok', text: data.message });
    } catch (err) {
      setReloadMsg({ ok: false, text: (err as Error).message });
    } finally {
      setReloading(false);
      // Auto-dismiss après 4s
      setTimeout(() => setReloadMsg(null), 4_000);
    }
  }, [sdk]);

  return (
    <div className="space-y-6">
      {/* ── En-tête ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gouvernance</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Règles d'accès aux modèles LLM par niveau de classification · Proxy réseau CAA
          </p>
        </div>
        <button
          onClick={handleReloadPolicy}
          disabled={reloading}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors disabled:opacity-50"
          title="Recharger policy.yaml sans restart serveur"
        >
          <RefreshCw size={14} className={reloading ? 'animate-spin' : ''} />
          {reloading ? 'Rechargement…' : 'Recharger la politique'}
        </button>
      </div>

      {/* ── Feedback reload ── */}
      {reloadMsg && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium ${
          reloadMsg.ok
            ? 'bg-green-500/10 text-green-700 border border-green-500/20'
            : 'bg-destructive/10 text-destructive border border-destructive/20'
        }`}>
          {reloadMsg.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {reloadMsg.text}
        </div>
      )}

      {/* ── Règles d'accès par clearance ── */}
      <section>
        <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
          <Shield size={16} className="text-primary" />
          Règles d'accès modèles LLM par classification
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MODEL_ACCESS_RULES.map((rule) => (
            <ModelAccessCard key={rule.clearance} rule={rule} />
          ))}
        </div>
      </section>

      {/* ── Proxy CAA ── */}
      <section>
        <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
          <Server size={16} className="text-primary" />
          Infrastructure réseau
        </h2>
        <div className="max-w-lg">
          <ProxyGouvernanceWidget config={PROXY_CONFIG} />
        </div>
      </section>

      {/* ── Note réglementaire ── */}
      <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
        <div className="flex items-start gap-3">
          <ShieldOff size={18} className="text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Contrainte réglementaire C4 / ANSSI</p>
            <p className="text-xs text-red-600 mt-1">
              Toute donnée classifiée C4 (médicale) doit être traitée exclusivement sur l'infrastructure
              On-Prem CAA (cluster Mistral local). Le routage vers un provider cloud est techniquement
              bloqué par le <code className="bg-red-500/10 px-1 rounded">PolicyEngine</code> avant
              l'exécution du pipeline — cette règle ne peut pas être contournée depuis l'interface.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
